import { call, put, select, takeEvery } from "redux-saga/effects";
import { getForensicSearchStatusResponse,
  createForensicSearchResponse,
  deleteForensicSearchResponse, 
  fetchedForensicSearchResults,
  getImageUrlFromForensicSearchImageIndexResponse,
   types } from "../actions";
import { createForensicSearch,
  deleteForensicSearch,
  getForensicSearchStatus,
  getRecentForensicSearches,
  getImagesFromImageIndexes } from "../../api/ForensicSearchServiceManager";
import { flattenSearchResults } from "../../pages/HistoricalSearch/ForensicSearchUtil";
import { FORENSIC_SEARCH_SAGA_STATUS_FAILED, FORENSIC_SEARCH_SAGA_MESSAGES } from "../../pages/HistoricalSearch/SearchConstants";

const activeSession = (state) => state.sessionReducer.activeSession;

function* ForensicSearchSaga() {
  yield takeEvery(types.CREATE_FORENSIC_SEARCH_RESULTS, createForensicSearchResult);
  yield takeEvery(types.GET_FORENSIC_SEARCH_STATUS_REQUEST, getForensicCreatedSearchStatus);
  yield takeEvery(types.GET_RECENT_FORENSIC_SEARCHES_REQUEST, fetchRecentForensicSearches);
  yield takeEvery(types.DELETE_FORENSIC_SEARCH_RESULTS, deleteForensicSearchResult);
  yield takeEvery(types.GET_IMAGE_URL_FORENSIC_SEARCH_IMAGE_INDEX, getimageForensicSearchImageIndex);
}

function* createForensicSearchResult(action) {
  try {
    const session = yield select(activeSession);
    const searchResult = yield call(createForensicSearch, action.payload, session.activeSession );

    if(searchResult?.status === 200){
        yield put(createForensicSearchResponse({'statusCode': 200, 'responseData': searchResult.data}));
    } else {
        const serverMessage = searchResult?.status === 409
          ? searchResult?.data?.original_error_description || searchResult?.data?.message || searchResult?.statusText || ""
          : searchResult?.data?.message || searchResult?.data?.error || searchResult?.statusText || "";
        yield put(createForensicSearchResponse({'statusCode': searchResult?.status || 500, 'responseData': null, 'statusText': serverMessage}));
    }

  } catch (error) {
    console.log("Error while creating forensic search -> " + error);
    yield put(createForensicSearchResponse({'statusCode': 500, 'responseData': null, 'statusText': String(error)}));
  }
}
export function* deleteForensicSearchResult(action) {
  try {
    const session = yield select(activeSession);
    const searchResult = yield call(deleteForensicSearch, action.payload, session.activeSession );

    if(searchResult?.status === 200){
        yield put(deleteForensicSearchResponse({'statusCode': searchResult?.status, 'statusText': FORENSIC_SEARCH_SAGA_MESSAGES.SEARCH_DELETE_SUCCESS, 'responseData': {id: action.payload.id}}));
    }else{
      let action_payload = action.payload;
      action_payload.message = FORENSIC_SEARCH_SAGA_MESSAGES.SEARCH_DELETE_FAILED;
      action_payload.status = FORENSIC_SEARCH_SAGA_STATUS_FAILED;
    yield put(deleteForensicSearchResponse({'statusCode':searchResult?.status,'responseData': action_payload,'statusText': FORENSIC_SEARCH_SAGA_MESSAGES.SEARCH_DELETE_FAILED_STATUSTEXT}));
    }

  } catch (error) {
    console.log("Error while deleting forensic search -> " + error);
    let action_payload = action.payload;
    action_payload.message = FORENSIC_SEARCH_SAGA_MESSAGES.SEARCH_DELETE_FAILED;
    action_payload.status = FORENSIC_SEARCH_SAGA_STATUS_FAILED;
    yield put(deleteForensicSearchResponse({'statusCode': 500, 'responseData': action_payload, 'statusText': String(error)}));
  }
}

function* getForensicCreatedSearchStatus(action) {
  try {
    const session = yield select(activeSession);
    const searchResult = yield call(getForensicSearchStatus, action.payload, session.activeSession );

    if(searchResult?.status === 200){
        yield put(getForensicSearchStatusResponse({'statusCode': 200, 'responseData': searchResult.data}));
    } else {
        yield put(getForensicSearchStatusResponse({'statusCode': searchResult?.status || 500, 'responseData': null}));
    }

  } catch (error) {
    console.log("Error while getting forensic search status -> " + error);
    yield put(getForensicSearchStatusResponse({'statusCode': 500, 'responseData': null}));
  }
}
function* fetchRecentForensicSearches(action) {
  try {
    const session = yield select(activeSession);
    const searchResult = yield call(getRecentForensicSearches, action.payload, session.activeSession );
    // id present -> this is a single-search fetch (resourceidentifier), not the full recent-searches list.
    const isSingleFetch = !!action.payload?.id;

    if(searchResult?.status === 200){
        const searches = Array.isArray(searchResult.data) ? searchResult.data : [searchResult.data];
        // Guard against a 200 response whose body isn't actually search data (e.g. a proxy/network
        // error string returned with a success status) - drop anything that isn't a real search object.
        const validSearches = searches.filter((s) => {
          const isValid = s && typeof s === "object" && !Array.isArray(s) && s.id;
          if (!isValid) {
            console.warn("Dropped invalid search result from backend:", s);
          }
          return isValid;
        });
        // TODO: remove requestedId once backend guarantees a single record for a resourceidentifier fetch.
        yield put(fetchedForensicSearchResults({'statusCode': 200, 'responseData': validSearches.map(flattenSearchResults), isSingleFetch, requestedId: action.payload?.id}));
    } else {
        yield put(fetchedForensicSearchResults({'statusCode': searchResult?.status || 500, 'statusText': searchResult?.statusText, 'responseData': [], isSingleFetch}));
    }

  } catch (error) {
    console.log("Error while fetching recent forensic searches -> " + error);
    yield put(fetchedForensicSearchResults({'statusCode': 500, 'statusText': String(error), 'responseData': [], isSingleFetch: !!action.payload?.id}));
  }
}
function* getimageForensicSearchImageIndex(action) {
  try {
    const session = yield select(activeSession);
    const searchResult = yield call(getImagesFromImageIndexes, action.payload, session.activeSession );

    if(searchResult?.status === 200){
        // Keep the original request's id/searchIndex(fovid) alongside the actual image data on
        // success too - callers need to know WHICH fovid this response belongs to (mirrors the
        // failure branch below), since multiple fovs can have requests in flight concurrently.
        yield put(getImageUrlFromForensicSearchImageIndexResponse({'statusCode': searchResult?.status, 'statusText': FORENSIC_SEARCH_SAGA_MESSAGES.IMAGE_INDEX_FETCH_SUCCESS, 'responseData': {...action.payload, images: searchResult?.data}}));
    }else{
      let action_payload = action.payload;
      action_payload.message = FORENSIC_SEARCH_SAGA_MESSAGES.IMAGE_INDEX_FETCH_FAILED;
      action_payload.status = FORENSIC_SEARCH_SAGA_STATUS_FAILED;
    yield put(getImageUrlFromForensicSearchImageIndexResponse({'statusCode':searchResult?.status,'responseData': action_payload,'statusText': FORENSIC_SEARCH_SAGA_MESSAGES.IMAGE_INDEX_FETCH_FAILED_STATUSTEXT}));
    }

  } catch (error) {
    console.log("Error while fetching image index -> " + error);
    let action_payload = action.payload;
    action_payload.message = FORENSIC_SEARCH_SAGA_MESSAGES.IMAGE_INDEX_FETCH_FAILED;
    action_payload.status = FORENSIC_SEARCH_SAGA_STATUS_FAILED;
    yield put(getImageUrlFromForensicSearchImageIndexResponse({'statusCode': 500, 'responseData': action_payload, 'statusText': String(error)}));
  }
}


export default ForensicSearchSaga;